import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Shield, Key, UserCog, X, AlertTriangle, Settings, ToggleLeft, ToggleRight, Search, UserPlus, Clock, Check, Bell, Send, MapPin } from 'lucide-react';
import { useClub } from '../App';

const CONFIG_ITEMS = [
  { key: 'require_email', label: 'Require Email', desc: 'Email is mandatory for registration and player creation' },
  { key: 'require_phone', label: 'Require Phone', desc: 'Phone is mandatory for registration and player creation' },
  { key: 'require_nickname', label: 'Require Nickname', desc: 'Nickname is mandatory for registration and player creation' },
  { key: 'unique_email', label: 'Unique Email', desc: 'Each email can only belong to one user' },
  { key: 'unique_phone', label: 'Unique Phone', desc: 'Each phone number can only belong to one user (when provided)' },
  { key: 'unique_nickname', label: 'Unique Nickname', desc: 'Each nickname must be globally unique' },
  { key: 'require_questionnaire', label: 'Require Skill Questionnaire', desc: 'New users must complete the 10-question skill assessment before using the app' },
];

function ResetPasswordModal({ user: targetUser, onClose }) {
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/admin/users/${targetUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${targetUser.username}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-900">Reset Password</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="close-reset-modal"><X size={16} /></button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Set a new password for <span className="font-semibold text-slate-700">{targetUser.username}</span>
        </p>
        <form onSubmit={handleReset} className="space-y-3" data-testid="reset-password-form">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="new-password-input"
              autoComplete="new-password"
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 h-8 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg transition-all">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 h-8 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg transition-all" data-testid="confirm-reset-password-btn">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function WipeDatabaseModal({ onClose, onWiped }) {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleWipe = async () => {
    if (confirmText !== 'WIPE EVERYTHING') {
      toast.error('Type "WIPE EVERYTHING" to confirm');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/admin/wipe-database', { confirm: confirmText });
      toast.success(res.data.message);
      onWiped();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to wipe database');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div className="bg-white rounded-2xl border-2 border-red-200 shadow-xl w-full max-w-sm p-5" data-testid="wipe-confirm-modal">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-red-600" />
          </div>
          <h3 className="text-sm font-bold text-red-800">Wipe Database</h3>
        </div>
        <p className="text-xs text-slate-600 mb-1">
          This will permanently delete <span className="font-bold text-red-700">all data</span>:
        </p>
        <ul className="text-[10px] text-slate-500 mb-3 space-y-0.5 ml-3">
          <li>All clubs and memberships</li>
          <li>All players and ratings</li>
          <li>All tournaments and games</li>
          <li>All notifications and invite links</li>
        </ul>
        <p className="text-[10px] text-slate-600 mb-2">
          Type <code className="bg-red-50 text-red-700 px-1 py-0.5 rounded font-mono text-[10px] font-bold">WIPE EVERYTHING</code> to confirm:
        </p>
        <input
          type="text"
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder="WIPE EVERYTHING"
          className="w-full h-8 rounded-lg border border-red-200 px-3 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-red-400 mb-3"
          data-testid="wipe-confirm-input"
        />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-8 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg transition-all" data-testid="cancel-wipe-btn">
            Cancel
          </button>
          <button
            onClick={handleWipe}
            disabled={loading || confirmText !== 'WIPE EVERYTHING'}
            className="flex-1 h-8 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg transition-all"
            data-testid="confirm-wipe-btn"
          >
            {loading ? 'Wiping...' : 'Wipe Database'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { selectedClub, clearClub } = useClub();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [changingRole, setChangingRole] = useState(null);
  const [showWipeConfirm, setShowWipeConfirm] = useState(false);
  const [cfg, setCfg] = useState({});
  const [cfgLoading, setCfgLoading] = useState(true);
  const [cfgSaving, setCfgSaving] = useState(null);

  // Join Requests
  const [joinRequests, setJoinRequests] = useState([]);
  const [actingOnReq, setActingOnReq] = useState(null);

  // Mass Notify
  const [showMassNotify, setShowMassNotify] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifySending, setNotifySending] = useState(false);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/config');
      setCfg(res.data);
    } catch {} finally { setCfgLoading(false); }
  };

  const toggleConfig = async (key) => {
    const newVal = !cfg[key];
    setCfgSaving(key);
    try {
      const res = await api.patch('/config', { [key]: newVal });
      setCfg(res.data.config);
      toast.success(`${key.replace(/_/g, ' ')} ${newVal ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setCfgSaving(null); }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); fetchConfig(); fetchJoinRequests(); }, []);

  const fetchJoinRequests = async () => {
    if (!selectedClub) return;
    try {
      const res = await api.get(`/clubs/${selectedClub.id}/join-requests`);
      setJoinRequests(res.data);
    } catch {}
  };

  const handleJoinReq = async (reqId, action) => {
    setActingOnReq(reqId);
    try {
      await api.post(`/clubs/join-requests/${reqId}/${action}`);
      toast.success(action === 'approve' ? 'Player approved!' : 'Request rejected');
      fetchJoinRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActingOnReq(null); }
  };

  const sendMassNotify = async () => {
    if (!notifyTitle || !notifyMsg) { toast.error('Title and message are required'); return; }
    setNotifySending(true);
    try {
      const res = await api.post(`/clubs/${selectedClub.id}/mass-notify`, { title: notifyTitle, message: notifyMsg });
      toast.success(res.data.message);
      setShowMassNotify(false);
      setNotifyTitle('');
      setNotifyMsg('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setNotifySending(false); }
  };

  const changeRole = async (uid, newRole) => {
    setChangingRole(uid);
    try {
      await api.patch(`/admin/users/${uid}/role`, { role: newRole });
      toast.success('Role updated');
      setUsers(us => us.map(u => u.id === uid ? { ...u, role: newRole } : u));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setChangingRole(null);
    }
  };

  const roleColor = {
    admin: 'bg-green-50 text-green-700 border-green-200',
    contributor: 'bg-blue-50 text-blue-700 border-blue-200',
    player: 'bg-slate-50 text-slate-600 border-slate-200',
  };

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Panel</h1>
          <p className="text-[11px] text-slate-500">Manage users, roles, and settings</p>
        </div>
        <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{users.length} users</span>
      </div>

      {/* User Management Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="admin-users-table">
        <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-1.5">
          <Shield size={13} className="text-green-600" />
          <span className="text-xs font-bold text-slate-900">User Management</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-2 py-1.5 text-left text-[9px] font-semibold text-slate-500 uppercase">User</th>
                <th className="px-1 py-1.5 text-left text-[9px] font-semibold text-slate-500 uppercase">Role</th>
                <th className="px-1 py-1.5 text-left text-[9px] font-semibold text-slate-500 uppercase">Player ID</th>
                <th className="px-1 py-1.5 text-right text-[9px] font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-2 py-2"><div className="h-3 bg-slate-100 rounded w-24 animate-pulse" /></td>
                    <td className="px-1 py-2"><div className="h-3 bg-slate-100 rounded w-14 animate-pulse" /></td>
                    <td className="px-1 py-2"><div className="h-3 bg-slate-100 rounded w-16 animate-pulse" /></td>
                    <td className="px-1 py-2"><div className="h-3 bg-slate-100 rounded w-20 ml-auto animate-pulse" /></td>
                  </tr>
                ))
              ) : (
                users.map((u, i) => (
                  <tr key={u.id} className={`hover:bg-slate-50 transition-colors ${i < users.length - 1 ? 'border-b border-slate-100' : ''}`} data-testid={`user-row-${u.id}`}>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold text-[9px] flex-shrink-0">
                          {u.username?.[0]?.toUpperCase() || '?'}
                        </div>
                        <span className="text-[10px] font-medium text-slate-900">{u.username}</span>
                      </div>
                    </td>
                    <td className="px-1 py-1.5">
                      <select
                        value={u.role}
                        onChange={e => changeRole(u.id, e.target.value)}
                        disabled={changingRole === u.id}
                        className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium border cursor-pointer focus:outline-none focus:ring-1 focus:ring-green-500 ${roleColor[u.role] || ''}`}
                        data-testid={`role-select-${u.id}`}
                      >
                        <option value="admin">Admin</option>
                        <option value="contributor">Contributor</option>
                        <option value="player">Player</option>
                      </select>
                    </td>
                    <td className="px-1 py-1.5">
                      <span className="text-[9px] text-slate-400 font-mono">{u.player_id || '—'}</span>
                    </td>
                    <td className="px-1 py-1.5 text-right">
                      <button
                        onClick={() => setResetTarget(u)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[9px] font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all"
                        data-testid={`reset-password-btn-${u.id}`}
                      >
                        <Key size={10} /> Reset
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {resetTarget && (
        <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />
      )}

      {/* Join Requests */}
      {joinRequests.length > 0 && (
        <div className="bg-white rounded-xl border border-green-200 shadow-sm overflow-hidden" data-testid="join-requests">
          <div className="px-3 py-2 border-b border-green-100 bg-green-50 flex items-center gap-1.5">
            <UserPlus size={13} className="text-green-600" />
            <span className="text-xs font-bold text-green-800">Pending Join Requests</span>
            <span className="text-[10px] text-green-600 ml-auto">{joinRequests.length}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {joinRequests.map(r => (
              <div key={r.id} className="px-3 py-2 flex items-center gap-2" data-testid={`join-req-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-medium text-slate-900 truncate">{r.player_name} <span className="text-[9px] text-slate-400 font-mono">({r.player_rating})</span></p>
                  {r.message && <p className="text-[9px] text-slate-500 truncate">{r.message}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => handleJoinReq(r.id, 'approve')} disabled={actingOnReq === r.id}
                    className="h-6 px-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-[9px] font-semibold rounded-lg"
                    data-testid={`approve-${r.id}`}><Check size={10} /></button>
                  <button onClick={() => handleJoinReq(r.id, 'reject')} disabled={actingOnReq === r.id}
                    className="h-6 px-2 bg-white border border-slate-200 hover:bg-red-50 text-slate-500 text-[9px] rounded-lg"
                    data-testid={`reject-${r.id}`}><X size={10} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mass Notify */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-3 py-2.5 flex items-center gap-2 flex-wrap">
        <Bell size={13} className="text-amber-500 flex-shrink-0" />
        <span className="text-xs font-semibold text-slate-700 flex-shrink-0">Club Announcement</span>
        <button onClick={() => setShowMassNotify(!showMassNotify)}
          className="ml-auto h-7 px-3 bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-semibold rounded-lg transition-all"
          data-testid="mass-notify-btn">
          {showMassNotify ? 'Cancel' : 'Send to All'}
        </button>
      </div>

      {showMassNotify && (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-3" data-testid="mass-notify-form">
          <p className="text-[10px] font-bold text-slate-700 mb-2">Send to All Club Members</p>
          <input type="text" value={notifyTitle} onChange={e => setNotifyTitle(e.target.value)}
            placeholder="Notification title (e.g., Saturday Session)"
            className="w-full h-8 rounded-lg border border-slate-200 px-3 text-xs mb-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="notify-title-input" />
          <textarea value={notifyMsg} onChange={e => setNotifyMsg(e.target.value)}
            placeholder="Message (e.g., Are you available this Saturday 3-5pm?)"
            className="w-full h-16 rounded-lg border border-slate-200 px-3 py-2 text-xs mb-2 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
            data-testid="notify-msg-input" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowMassNotify(false)} className="h-7 px-3 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-lg">Cancel</button>
            <button onClick={sendMassNotify} disabled={notifySending}
              className="h-7 px-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white text-[10px] font-semibold rounded-lg flex items-center gap-1"
              data-testid="send-notify-btn">
              <Send size={10} /> {notifySending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {/* App Settings */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="app-settings">
        <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-1.5">
          <Settings size={13} className="text-green-600" />
          <span className="text-xs font-bold text-slate-900">App Settings</span>
          <span className="text-[9px] text-slate-400 ml-auto">Changes apply immediately</span>
        </div>
        <div className="divide-y divide-slate-100">
          {cfgLoading ? (
            <div className="px-3 py-6 text-center text-xs text-slate-400">Loading settings...</div>
          ) : (
            CONFIG_ITEMS.map(item => (
              <div key={item.key} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`config-${item.key}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-slate-800">{item.label}</p>
                  <p className="text-[9px] text-slate-500 leading-tight">{item.desc}</p>
                </div>
                <button
                  onClick={() => toggleConfig(item.key)}
                  disabled={cfgSaving === item.key}
                  className={`flex-shrink-0 transition-all ${cfgSaving === item.key ? 'opacity-50' : ''}`}
                  data-testid={`toggle-${item.key}`}
                >
                  {cfg[item.key] ? (
                    <ToggleRight size={26} className="text-green-500" />
                  ) : (
                    <ToggleLeft size={26} className="text-slate-300" />
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-xl border-2 border-red-200 shadow-sm overflow-hidden" data-testid="danger-zone">
        <div className="px-3 py-2 border-b border-red-100 bg-red-50 flex items-center gap-1.5">
          <AlertTriangle size={13} className="text-red-600" />
          <span className="text-xs font-bold text-red-800">Danger Zone</span>
        </div>
        <div className="p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold text-slate-900">Wipe Database</p>
              <p className="text-[9px] text-slate-500 mt-0.5 max-w-xs leading-tight">
                Permanently delete all clubs, players, tournaments, games, ratings, and notifications. Only admin account survives. Cannot be undone.
              </p>
            </div>
            <button
              onClick={() => setShowWipeConfirm(true)}
              className="flex-shrink-0 h-7 px-3 text-[10px] font-medium text-red-600 border border-red-300 bg-white hover:bg-red-50 rounded-lg transition-all"
              data-testid="wipe-database-btn"
            >
              Wipe
            </button>
          </div>
        </div>
      </div>

      {showWipeConfirm && (
        <WipeDatabaseModal onClose={() => setShowWipeConfirm(false)} onWiped={() => { setShowWipeConfirm(false); clearClub(); }} />
      )}
    </div>
  );
}
